import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, Edit, Calendar, Key, Shield } from 'lucide-react';
import { Button, Badge } from '../../common';
import tenantsService from '../../../services/api/tenants.service';

const TenantDetailsModal = ({ tenantId, onClose, onEdit }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    loadTenantDetails();
  }, [tenantId]);

  const loadTenantDetails = async () => {
    try {
      setLoading(true);
      const data = await tenantsService.getById(tenantId);
      setTenant(data);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      alert('Erro ao carregar detalhes do tenant');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (field, value) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (date) => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  const InfoField = ({ label, value, secret = false, copyable = false }) => (
    <div className="p-4 bg-gray-50 rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">{label}</p>
          <p className={`text-sm font-mono break-all ${secret ? 'blur-sm hover:blur-none transition-all' : 'text-gray-900'}`}>
            {value || <span className="text-gray-400 italic">Não definido</span>}
          </p>
        </div>
        {copyable && value && (
          <button
            onClick={() => handleCopy(label, value)}
            className="ml-2 p-1.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
            title="Copiar"
          >
            {copiedField === label ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-gray-400" />
            )}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-primary-100">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">{tenant.name}</h2>
            <p className="text-sm text-gray-600 mt-1">ID: {tenant.id}</p>
            <div className="flex items-center gap-2 mt-3">
              <Badge variant={tenant.isActive ? 'success' : 'danger'} dot>
                {tenant.isActive ? 'Ativo' : 'Inativo'}
              </Badge>
              <Badge variant={tenant.botPaused ? 'warning' : 'primary'}>
                {tenant.botPaused ? 'Bot Pausado' : 'Bot Ativo'}
              </Badge>
              <Badge variant={tenant.waProvider === 'official' ? 'primary' : 'secondary'}>
                {tenant.waProvider === 'official' ? 'WhatsApp Official' : 'Evolution API'}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                onEdit(tenant);
                onClose();
              }}
              leftIcon={<Edit className="w-4 h-4" />}
            >
              Editar
            </Button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] custom-scrollbar">
          <div className="space-y-6">
            {/* Informações Básicas */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-500" />
                Informações Básicas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField label="Nome" value={tenant.name} />
                <InfoField label="Email" value={tenant.email} copyable />
                <InfoField label="ID" value={tenant.id} copyable />
                <InfoField label="Provider" value={tenant.waProvider?.toUpperCase()} />
                <InfoField label="Default Flow ID" value={tenant.defaultFlowId} />
                <InfoField label="Password Hash" value={tenant.passwordHash} secret copyable />
              </div>
            </div>

            {/* WhatsApp Official Config */}
            {tenant.waProvider === 'official' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações WhatsApp Official</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoField label="Phone Number ID" value={tenant.waPhoneNumberId} copyable />
                  <InfoField label="Graph API Version" value={tenant.graphApiVersion} />
                  <InfoField label="Access Token" value={tenant.waAccessToken} secret copyable />
                  <InfoField label="App Secret" value={tenant.waAppSecret} secret copyable />
                  <InfoField label="Template Name" value={tenant.waTemplateName} />
                  <InfoField label="Template Language" value={tenant.waTemplateLang} />
                </div>
              </div>
            )}

            {/* Evolution API Config */}
            {tenant.waProvider === 'evolution' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Evolution API</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoField label="Instance Name" value={tenant.evolutionInstance} copyable />
                  <InfoField label="API URL" value={tenant.evolutionApiUrl} copyable />
                  <InfoField label="API Key" value={tenant.evolutionApiKey} secret copyable />
                </div>
              </div>
            )}

            {/* Coexistência */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Coexistência com Agentes</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InfoField 
                  label="Coexistência Habilitada" 
                  value={tenant.coexEnable ? 'Sim' : 'Não'} 
                />
                <InfoField 
                  label="Auto Resume (minutos)" 
                  value={tenant.coexAutoResumeM?.toString() || '0'} 
                />
                <InfoField 
                  label="Ignore Bot Messages (ms)" 
                  value={tenant.coexIgnoreBotMs?.toString() || '5000'} 
                />
              </div>
            </div>

            {/* Status e Flags */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status e Controles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Tenant Ativo</p>
                  <p className={`text-sm font-semibold ${tenant.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {tenant.isActive ? '✓ Sim' : '✗ Não'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Bot Pausado</p>
                  <p className={`text-sm font-semibold ${tenant.botPaused ? 'text-yellow-600' : 'text-green-600'}`}>
                    {tenant.botPaused ? '⏸ Sim' : '▶ Não'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Envio Desabilitado</p>
                  <p className={`text-sm font-semibold ${tenant.sendDisabled ? 'text-red-600' : 'text-green-600'}`}>
                    {tenant.sendDisabled ? '✗ Sim' : '✓ Não'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Protocolo Habilitado</p>
                  <p className={`text-sm font-semibold ${tenant.sendProtocol ? 'text-green-600' : 'text-gray-600'}`}>
                    {tenant.sendProtocol ? '✓ Sim' : '✗ Não'}
                  </p>
                </div>
              </div>
            </div>

            {/* Segurança */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-primary-500" />
                Segurança
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <InfoField 
                  label="Tenant JWT Secret" 
                  value={tenant.tenantJwtSecret} 
                  secret 
                  copyable 
                />
              </div>
            </div>

            {/* Timestamps */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary-500" />
                Datas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField 
                  label="Criado em" 
                  value={formatDate(tenant.createdAt)} 
                />
                <InfoField 
                  label="Último login" 
                  value={formatDate(tenant.lastLoginAt)} 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500">
            💡 Campos com <span className="blur-sm">blur</span> são sensíveis. Passe o mouse para visualizar.
          </p>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TenantDetailsModal;